import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import SchoolIcon from "@mui/icons-material/SchoolOutlined";
import DashboardMenu from "./DashboardMenu";
import SchoolNavContents from "./SchoolNavContents";
import UserMenu from "./UserMenu";
import NavDrawerButton from "../shared/NavDrawerButton";
import NavActionBar from "./NavActionBar";
import useNotificationsCount from "../../hooks/useNotificationsCount";
import useUnreadMessagesCount from "../../hooks/useUnreadMessagesCount";

export default function SchoolNavbar({
  appProps,
  schoolSlug,
  schoolYearId = null,
  termId = null,
  navigationMode,
  linkComponent = "a",
  linkProps = {},
  leftExtras = null,
  rightExtras = null,
  loadingSlot = null,
  showNavDrawer = true,
  sticky = true,
  dashboardOpen: dashboardOpenProp,
  dashboardManage = false,
  onDashboardOpenChange,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [scrolled, setScrolled] = useState(false);
  const [internalDashboardOpen, setInternalDashboardOpen] = useState(false);
  const isDashboardControlled = dashboardOpenProp !== undefined;
  const dashboardOpen = isDashboardControlled
    ? dashboardOpenProp
    : internalDashboardOpen;
  const setDashboardOpen = (next) => {
    if (!isDashboardControlled) setInternalDashboardOpen(next);
    onDashboardOpenChange?.(next);
  };

  // Cmd+K (Mac) / Ctrl+K (Windows/Linux) toggles the Dashboard launchpad from
  // anywhere. A ref mirrors the current open state so the listener reads fresh
  // state without re-subscribing on every toggle. The combo uses a modifier, so
  // it intentionally fires even while focus is in an input (command-palette
  // convention); preventDefault stops the browser's search/address-bar default.
  const dashboardOpenRef = useRef(dashboardOpen);
  dashboardOpenRef.current = dashboardOpen;
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.repeat) return;
      const isCmdK =
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        !e.altKey &&
        e.key.toLowerCase() === "k";
      if (!isCmdK) return;
      e.preventDefault();
      setDashboardOpen(!dashboardOpenRef.current);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    // The scroll shadow only matters while the navbar is pinned; when it scrolls
    // away with the page (sidebar mode) there is nothing to cue.
    if (!sticky) return undefined;
    const handleScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sticky]);

  const schoolName = appProps?.school?.name ?? "";
  const notificationsCount = useNotificationsCount(appProps);
  const messagesCount = useUnreadMessagesCount(appProps, schoolSlug);
  const navigate = useNavigate();

  // Messages/Notifications are v2 destinations. Inside the SPA (navigationMode
  // undefined) navigate client-side; when this same navbar is bridged onto a
  // legacy page (navigationMode "browser", a MemoryRouter decoupled from the
  // address bar) do a full-document load to cross into the SPA. Mirrors
  // SchoolNavMenu.handleNavigate, and covers the desktop icons, the mobile
  // UserMenu items (via onNavigate), and the bridged copy in one place.
  // Navigate to an absolute SPA url, honouring the bridge: a full-document load
  // when this navbar is mounted on a legacy page (MemoryRouter), a client-side
  // navigation inside the SPA.
  const goToUrl = (url) => {
    if (navigationMode === "browser") window.location.assign(url);
    else navigate(url);
  };
  const goTo = (path) => {
    if (!schoolSlug) return;
    goToUrl(`/v2/school/${schoolSlug}${path}`);
  };

  // Action bar (Notifications, Dashboard, Messages). Rendered once and placed
  // in the navbar's center slot: inline on desktop, floated to the bottom of
  // the viewport on mobile via the wrapper's fixed positioning. It rides along
  // with this navbar so it shows on both the SPA and the legacy pages this
  // chrome is bridged onto. Tabs: Notifications (0), Dashboard (1, the launchpad
  // overlay), Messages (2). The indicator tracks the current route; on bridged
  // legacy pages the real path lives on window.location, not the decoupled
  // MemoryRouter location.
  const location = useLocation();
  const currentPath =
    navigationMode === "browser" ? window.location.pathname : location.pathname;
  const getBottomNavIndex = () => {
    if (currentPath.includes("/notifications")) return 0;
    if (currentPath.includes("/messages")) return 2;
    return 1;
  };
  const handleBottomNav = (index) => {
    if (index === 0) goTo("/notifications");
    else if (index === 2) goTo("/messages/inbox");
    else setDashboardOpen(true);
  };
  // Live counters, index-matched to the bar's tabs: Notifications (0),
  // Dashboard (1, no badge), Messages (2).
  const bottomNavBadges = [notificationsCount, 0, messagesCount];

  // The hamburger + its drawer, on the far left of the navbar (mobile and
  // desktop). On mobile it takes the place of the school hat icon; the floating
  // action bar still carries the primary actions. Hidden in sidebar mode
  // (showNavDrawer false), where the permanent sidebar holds the nav contents.
  const navDrawerButton = showNavDrawer ? (
    <NavDrawerButton ariaLabel="Open navigation menu" anchor="left">
      <SchoolNavContents
        appProps={appProps}
        schoolSlug={schoolSlug}
        schoolYearId={schoolYearId}
        termId={termId}
        navigationMode={navigationMode}
      />
    </NavDrawerButton>
  ) : null;

  return (
    <Box
      sx={{
        backgroundColor: (t) => t.custom.layoutBg.chrome,
        position: sticky ? "sticky" : "static",
        top: sticky ? 0 : "auto",
        zIndex: sticky ? 1000 : "auto",
        boxShadow: sticky && scrolled ? "0 0 10px rgba(0, 0, 0, 0.2)" : "none",
        transition: "box-shadow 0.3s ease-in-out",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          // Desktop keeps a tight vertical padding around the inline action bar;
          // mobile keeps the larger padding for the icon-only top bar.
          py: isMobile ? 1 : 0.5,
          px: 2,
          gap: 2,
        }}
      >
        {/* Left */}
        <Box
          sx={{
            // Desktop balances a fixed 40% against the right column so the
            // inline action bar stays centered. On mobile the action bar floats
            // out of flow, so the left side grows to fill the row and the name
            // clips right before the right-side account icon.
            width: isMobile ? "auto" : "40%",
            flex: isMobile ? 1 : "none",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 1 : 2,
            fontSize: 22,
            color: "#000000",
            "& a": { textDecoration: "none" },
          }}
        >
          {/* The hamburger (drawer trigger) leads the row on both mobile and
              desktop. Desktop then shows the school hat icon beside the name;
              mobile drops the hat since the hamburger already leads. The school
              year select follows on desktop only. (Fullscreen pages omit this
              whole navbar on mobile.) */}
          {navDrawerButton}
          <Box
            component={linkComponent}
            {...linkProps}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              textDecoration: "none",
              // minWidth: 0 lets this flex item shrink past its content so
              // the name ellipsis-clips instead of overflowing into the
              // right-side menu on narrow screens.
              minWidth: 0,
            }}
          >
            {!isMobile && (
              <SchoolIcon sx={{ color: "#00000080", flexShrink: 0 }} />
            )}
            <Box
              component="span"
              sx={{
                color: "#000000",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
                maxWidth: isMobile ? "none" : "250px",
              }}
            >
              {schoolName}
            </Box>
          </Box>
          {!isMobile && leftExtras}
        </Box>

        {/* Center: the action bar lives in the navbar center. On desktop it is
            inline and centered; on mobile the wrapper switches to fixed
            positioning so the bar floats at the bottom of the viewport. */}
        <Box
          sx={
            isMobile
              ? {
                  position: "fixed",
                  left: "50%",
                  transform: "translateX(-50%)",
                  bottom: "calc(16px + env(safe-area-inset-bottom))",
                  zIndex: 1200,
                }
              : { flexShrink: 0 }
          }
        >
          <NavActionBar
            value={getBottomNavIndex()}
            onChange={handleBottomNav}
            badges={bottomNavBadges}
            floating={isMobile}
            compact={!isMobile}
          />
        </Box>
        <DashboardMenu
          open={dashboardOpen}
          onClose={() => setDashboardOpen(false)}
          initialManage={dashboardManage}
          appProps={appProps}
          schoolSlug={schoolSlug}
          schoolYearId={schoolYearId}
          termId={termId}
          navigationMode={navigationMode}
        />

        {/* Right */}
        <Box
          sx={{
            // Desktop holds 40% to balance the centered action bar; mobile only
            // needs the account icon's width so the left name can run up to it.
            width: isMobile ? "auto" : "40%",
            flexShrink: 0,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
          }}
        >
          {rightExtras}
          {/* Messages + Notifications now live in the center action bar, so the
              right side is just the account menu. */}
          <UserMenu staff={appProps?.staff} />
        </Box>
      </Box>
      {loadingSlot}
    </Box>
  );
}
